import { useState, useEffect } from 'react';
import { deviceController } from '../../controllers/deviceController';
import { AlertTriangle, Bell, Filter } from 'lucide-react';

export const DeviceAlertsPage = () => {
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [filterSeverity, setFilterSeverity] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    deviceController.getAll().then(r => {
      if (r.success) {
        setDevices(r.data);
        const generated = [];
        r.data.forEach(d => {
          if (d.complianceStatus === 'Non-Compliant') generated.push({ id: `${d.id}-nc`, deviceId: d.id, deviceName: d.deviceName, assignedTo: d.assignedTo, type: 'Compliance Violation', message: 'Device is non-compliant with policy', severity: 'High', timestamp: d.lastSeen, status: 'Open' });
          if (d.batteryLevel < 15) generated.push({ id: `${d.id}-bat`, deviceId: d.id, deviceName: d.deviceName, assignedTo: d.assignedTo, type: 'Low Battery', message: `Battery at ${d.batteryLevel}%`, severity: 'Medium', timestamp: d.lastSeen, status: 'Open' });
          if (d.getDaysSinceLastSeen() > 7) generated.push({ id: `${d.id}-ls`, deviceId: d.id, deviceName: d.deviceName, assignedTo: d.assignedTo, type: 'Device Offline', message: `Not seen for ${d.getDaysSinceLastSeen()} days`, severity: 'Medium', timestamp: d.lastSeen, status: 'Open' });
          if (d.status === 'Lost') generated.push({ id: `${d.id}-lost`, deviceId: d.id, deviceName: d.deviceName, assignedTo: d.assignedTo, type: 'Device Lost', message: 'Device reported as lost or stolen', severity: 'Critical', timestamp: d.lastSeen, status: 'Open' });
        });
        setAlerts(generated);
      }
      setLoading(false);
    });
  }, []);

  const filtered = alerts.filter(a => filterSeverity === 'All' || a.severity === filterSeverity);
  const severityColor = { Critical: '#ef4444', High: '#f97316', Medium: '#f59e0b', Low: '#10b981' };

  const s = {
    page: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    titleWrap: { display: 'flex', alignItems: 'center', gap: '12px' },
    icon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#f9731620', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px', marginBottom: '22px' },
    statCard: { backgroundColor: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
    statLabel: { fontSize: '12px', color: '#64748b', marginBottom: '4px' },
    statVal: { fontSize: '24px', fontWeight: '700' },
    filterBar: { display: 'flex', gap: '10px', marginBottom: '16px' },
    select: { padding: '9px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    list: { display: 'flex', flexDirection: 'column', gap: '8px' },
    alertItem: { backgroundColor: '#fff', borderRadius: '10px', padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid #f1f5f9' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' },
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleWrap}>
          <div style={s.icon}><AlertTriangle size={22} color="#f97316" /></div>
          <div><h1 style={s.title}>Alerts & Violations</h1><div style={s.subtitle}>{alerts.filter(a => a.status === 'Open').length} open alerts</div></div>
        </div>
      </div>

      <div style={s.statsGrid}>
        {['Critical', 'High', 'Medium', 'Low'].map(sev => (
          <div key={sev} style={{ ...s.statCard, borderLeft: `3px solid ${severityColor[sev]}` }}>
            <div style={s.statLabel}>{sev}</div>
            <div style={{ ...s.statVal, color: severityColor[sev] }}>{alerts.filter(a => a.severity === sev).length}</div>
          </div>
        ))}
      </div>

      <div style={s.filterBar}>
        <select style={s.select} value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}>
          <option value="All">All Severity</option>
          {['Critical', 'High', 'Medium', 'Low'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading alerts...</div> : (
        <div style={s.list}>
          {filtered.length === 0 ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', backgroundColor: '#fff', borderRadius: '12px' }}>No alerts found. All devices are healthy! ✅</div> :
            filtered.map(alert => (
              <div key={alert.id} style={s.alertItem}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: `${severityColor[alert.severity]}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AlertTriangle size={16} color={severityColor[alert.severity]} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '13px', color: '#1e293b' }}>{alert.type} — {alert.deviceName}</div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{alert.message}</div>
                      {alert.assignedTo && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>User: {alert.assignedTo}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ ...s.badge, backgroundColor: `${severityColor[alert.severity]}15`, color: severityColor[alert.severity] }}>{alert.severity}</span>
                      <button style={{ fontSize: '11px', padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#fff', color: '#64748b' }}
                        onClick={() => setAlerts(prev => prev.filter(a => a.id !== alert.id))}>Dismiss</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
export default DeviceAlertsPage;
